import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Hammer, User, Search, Calendar, AlertTriangle } from "lucide-react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/dateFormat";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { NumberPlate } from "@/components/NumberPlate";

const FILTERS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "awaiting_parts", label: "Parts" },
  { key: "completed", label: "Completed" },
  { key: "invoiced", label: "Invoiced" },
  { key: "skipped", label: "Skipped" },
];

const PAY_TAG = {
  paid: { label: "Paid", cls: "bg-success/15 text-success" },
  partial: { label: "Partial", cls: "bg-amber-100 text-amber-700" },
  unpaid: { label: "Unpaid", cls: "bg-danger/10 text-danger" },
};
const PRIORITY_TAG = {
  urgent: { label: "Urgent", cls: "bg-amber-100 text-amber-700" },
  emergency: { label: "Emergency", cls: "bg-danger/10 text-danger" },
};

// Which jobs match a given filter key (same semantics as the old server calls).
const matchesStatus = (j, key) => {
  if (!key) return true;
  if (key === "active") return ["open", "in_progress", "awaiting_parts"].includes(j.status);
  if (key === "completed_today") {
    const today = new Date().toISOString().slice(0, 10);
    return j.status === "completed" && (j.completed_at || j.updated_at || "").slice(0, 10) === today;
  }
  if (key === "skipped") return (j.skipped_steps || []).some((s) => !s.completed_at);
  return j.status === key;
};

export default function JobsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const mine = params.get("mine") === "1";
  const [allJobs, setAllJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setAllJobs(await api("/jobs")); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const base = useMemo(() => allJobs.filter((j) => (!mine || j.assigned_to === user?.id)), [allJobs, mine, user?.id]);
  const counts = useMemo(() => {
    const c = {};
    for (const f of FILTERS) c[f.key] = base.filter((j) => matchesStatus(j, f.key)).length;
    return c;
  }, [base]);

  const jobs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return base.filter((j) => matchesStatus(j, status)).filter((j) => {
      if (!needle) return true;
      return [j.title, j.vehicle?.vrm, j.customer?.name, j.vehicle?.make, j.vehicle?.model]
        .some((v) => (v || "").toLowerCase().includes(needle));
    });
  }, [base, status, q]);

  const setFilter = (key) => {
    const next = {};
    if (key) next.status = key;
    if (mine) next.mine = "1";
    setParams(next);
  };
  const toggleMine = () => {
    const next = {};
    if (status) next.status = status;
    if (!mine) next.mine = "1";
    setParams(next);
  };

  return (
    <div>
      <PageHeader title="Jobs" subtitle="Track repairs across every stage"
        actions={<Link to="/jobs/new" data-testid="new-job-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"><Plus className="h-4 w-4" />New Job</Link>} />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="jobs-search"
          placeholder="Search plate, customer or job title…"
          className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key || "all"}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${status === f.key ? "bg-midnight text-white" : "bg-secondary text-slate-500 hover:bg-secondary/70"}`}>
            {f.label}
            <span data-testid={`filter-count-${f.key || "all"}`} className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${status === f.key ? "bg-white/20 text-white" : "bg-white text-slate-500"}`}>{counts[f.key] ?? 0}</span>
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <button onClick={toggleMine} data-testid="filter-mine"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${mine ? "bg-brand text-white" : "bg-secondary text-slate-500 hover:bg-secondary/70"}`}>
          <User className="h-3.5 w-3.5" />Assigned to me
        </button>
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> : jobs.length === 0 ? (
        <EmptyState icon={Hammer} title="No jobs here" description={q ? "No jobs match your search." : "No jobs in this status. Create one to start tracking work."}
          action={<Link to="/jobs/new" className="rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white">New Job</Link>} />
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {jobs.map((j) => {
            const isMine = j.assigned_to && j.assigned_to === user?.id;
            const pay = PAY_TAG[j.totals?.payment_status];
            const prio = PRIORITY_TAG[(j.priority || "").toLowerCase()];
            return (
            <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}
              className={`flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary ${isMine ? "border-l-4 border-brand bg-brand-tertiary/40" : ""}`}>
              {j.vehicle?.vrm ? <NumberPlate vrm={j.vehicle.vrm} size="sm" /> : <div className="h-7 w-24 rounded bg-secondary" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-bold text-midnight">{j.title}</p>
                  {prio && <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${prio.cls}`}><AlertTriangle className="h-2.5 w-2.5" />{prio.label}</span>}
                </div>
                <p className="truncate text-xs text-muted-foreground">{j.vehicle ? `${j.vehicle.make || ""} ${j.vehicle.model || ""}` : ""} · {j.customer?.name || "No customer"}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                  <span className="flex items-center gap-1" data-testid={`job-assignee-${j.id}`}>
                    <User className="h-3 w-3 text-muted-foreground" />
                    {j.assignee ? (
                      <span className={`font-semibold ${isMine ? "text-brand-2" : "text-midnight"}`}>{isMine ? "You" : (j.assignee.name || j.assignee.email)}</span>
                    ) : (
                      <span className="text-muted-foreground">Unassigned</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground"><Calendar className="h-3 w-3" />{fmtDate(j.created_at)}</span>
                </div>
              </div>
              <div className="hidden flex-col items-end gap-1 text-right sm:flex">
                <p className="font-display text-lg font-bold text-midnight">{money(j.totals?.total_gross)}</p>
                {pay && <span data-testid={`job-pay-${j.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pay.cls}`}>{pay.label}</span>}
              </div>
              <StatusChip status={j.status} />
            </button>
          );})}
        </div>
      )}
    </div>
  );
}
